'use client';

import React, { FC, useContext, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { Button, buttonVariants } from 'components/ui/button';
import type { VariantProps } from 'class-variance-authority';

import { BackButton } from 'app/_components/BackButton';
import { MemberTag } from 'app/(authenticated)/(member)/_components/MemberTag/MemberTags';
import { UserConnectType } from 'app/(authenticated)/(member)/_context/Member/Member';
import { useAuth } from 'contexts/auth';
import { ConfirmedEnum } from 'generated/types';
import { MemberContext } from '../../../_context/Member';
import { ConnectButton } from '../../../_components/ConnectButton';
import { TabIds } from '../../../my-profile/_components/ProfileForm';
import { InviteStatus } from 'types';
import { MEMBER_ROUTES, OWNER_ROUTES } from 'utils/routes';
import { useTracking } from 'app/_tracking';
import Link from 'next/link';
import NextImage from 'next/image';

interface ICVHeaderProps {
  user: UserConnectType;
  finishBtn?: any | null;
}

type ActionHeaderProps = ICVHeaderProps & {
  approveButtonLabel: string;
  declineButtonLabel: string;
  approveButtonVariant: VariantProps<typeof buttonVariants>['variant'];
  declineButtonVariant: VariantProps<typeof buttonVariants>['variant'];
  onClickApprove: () => void;
  onClickDecline: () => void;
};

const ActionHeader: FC<ActionHeaderProps> = (props) => {
  const {
    approveButtonLabel,
    declineButtonLabel,
    approveButtonVariant,
    declineButtonVariant,
    user,
    onClickApprove,
    onClickDecline
  } = props;

  const inviteStatus: InviteStatus = useMemo(() => {
    if (!!user?.connection?.workedWith) {
      return 'WorkedWithRequestNode';
    } else if (user?.connection?.connect) {
      return 'ConnectNode';
    } else if (!!user?.connection?.helpReaction) {
      return 'HelpReactionNode';
    }
  }, [user?.connection?.connect, user?.connection?.helpReaction, user?.connection?.workedWith]);

  const label: string = useMemo(() => {
    switch (inviteStatus) {
      case 'WorkedWithRequestNode':
        return 'Worked with you';
      case 'HelpReactionNode':
        return 'I can help you';
      case 'ConnectNode':
      default:
        return 'Let’s Connect';

    }
  }, [inviteStatus]);

  return (
    <>
      <div className="rounded-full bg-white px-4 py-2 flex gap-3 items-center">
        <p className="text-[12px]/[18px] font-medium">Send you: </p>
        <MemberTag label={label} type={inviteStatus} />
      </div>
      <div className="flex gap-[15px]">
        <Button variant={declineButtonVariant} size="small" onClick={onClickDecline}>
          {declineButtonLabel}
        </Button>
        <Button variant={approveButtonVariant} size="small" onClick={onClickApprove}>
          {approveButtonLabel}
        </Button>
      </div>
    </>
  );
};

const LetsConnect: FC<ICVHeaderProps> = (props) => {
  const { handleConnectConfirm } = useContext(MemberContext);

  const router = useRouter();

  return (
    <ActionHeader
      {...props}
      approveButtonLabel="Approve"
      declineButtonLabel="Decline"
      approveButtonVariant="primaryBrand"
      declineButtonVariant="tertiaryGray"
      onClickApprove={async () => {
        const isSuccess = await handleConnectConfirm(true, props.user?.connection?.connect?.connectId);

        if (isSuccess) {
          router.refresh();
        }
      }}
      onClickDecline={async () => {
        const isSuccess = await handleConnectConfirm(false, props.user?.connection?.connect?.connectId)

        if (isSuccess) {
          router.refresh();
        }
      }}
    />
  );
};

const WorkedWith: FC<ICVHeaderProps> = (props) => {
  const { handleWorkedWithConfirm } = useContext(MemberContext);

  const router = useRouter();

  return (
    <ActionHeader
      {...props}
      approveButtonLabel="Yes"
      declineButtonLabel="No"
      approveButtonVariant="green"
      declineButtonVariant="tertiaryGray"
      onClickApprove={async () => {
        const isSuccess = await handleWorkedWithConfirm(true, props.user?.connection?.workedWith?.workedWithId);

        if (isSuccess) {
          router.refresh();
        }
      }}
      onClickDecline={async () => {
        const isSuccess = await handleWorkedWithConfirm(false, props.user?.connection?.workedWith?.workedWithId);

        if (isSuccess) {
          router.refresh();
        }
      }}
    />
  );
};

const CanHelp: FC<ICVHeaderProps> = (props) => {
  return (
    <ActionHeader
      {...props}
      approveButtonLabel="Yes"
      declineButtonLabel="No"
      approveButtonVariant="orange"
      declineButtonVariant="tertiaryGray"
      onClickApprove={() => void 0}
      onClickDecline={() => void 0}
    />
  );
};

const CVControlPanel: FC<ICVHeaderProps> = (props) => {
  const router = useRouter();
  const { handleMessage } = useContext(MemberContext);
  const { currentUser } = useAuth();
  const { user } = props;

  if (props.finishBtn) {
    return (
      <Button
        size="small"
        variant="black"
        onClick={() => router.push(MEMBER_ROUTES.MEMBERS.getPath())}
      >
        Finish
      </Button>
    );
  }

  if (currentUser?.id === user.id) {
    return (
      <Button
        size="small"
        variant="black"
        onClick={() => router.push(`${MEMBER_ROUTES.EDIT_PROFILE.getPath()}?tab=${TabIds.GENERAL}`)}
      >
        Edit
      </Button>
    );
  }

  if (user?.connection?.connected) {
    return (
      <Button
        size="small"
        variant="darkBrand"
        onClick={() => handleMessage(user?.id ?? '')}
      >
        Message
      </Button>
    )
  } else {
    if (user?.connection?.connect?.confirmed === ConfirmedEnum.Pending && user.connection?.connect.fromThey) {
      return (
        <LetsConnect {...props} />
      );
    } else if (user?.connection?.connect && !user?.connection?.connect.fromThey) {
      return (
        <ConnectButton variant="nakedGrayLink" disabled user={user} size="small" classname="disabled:!px-0" />
      );
    }

    if (user?.connection?.workedWith?.confirmed === ConfirmedEnum.Pending && user?.connection?.workedWith.fromThey) {
      return (
        <WorkedWith {...props} />
      );
    } else if (user?.connection?.workedWith && !user?.connection?.workedWith.fromThey) {
      return (
        <ConnectButton variant="nakedGrayLink" disabled user={user} size="small" classname="disabled:!px-0" />
      );
    }

    if (user?.connection?.helpReaction) {
      return (
        <CanHelp {...props} />
      );
    }
  }

  return <ConnectButton variant="darkBrand" size="small" user={user} />;
};

export const CVHeaderHeader: FC<ICVHeaderProps> = (props) => {
  return (
    <div className='flex justify-between py-5 px-12 md:px-0 md:py-0'>
      <BackButton />

      <CVControlPanel {...props} />
    </div>
  );
};

export const MemberPublicHeader = () => {
  const { trackEvent } = useTracking();
  return (
    <header
      className='bg-gray-50 flex px-8 py-3 justify-between items-center md:px-3 md:py-[10px] w-full'>
      <div className={'flex flex-row min-w-[25%] justify-between items-center'}>
        <Link href='/'>
          <NextImage src={'/logo/kyg-logo-beta.svg'} width={90} height={35} alt='Logo' className='h-[35px] w-auto' />
        </Link>
        <Link
          href={MEMBER_ROUTES.DISCOVERY.getPath()}
          onClick={() => {
            trackEvent({ 'name': 'header_discovery_clicked' });
          }}
        >
          <Button
            variant={'nakedBrandLink'}
            size={'medium'}
          >
            Discovery Spaces
          </Button>
        </Link>
      </div>

      <div className={'min-w-[25%] flex justify-end'}>
        <Link href={OWNER_ROUTES.LOGIN.getPath()}>
          <Button
            variant='nakedGrayLink'
            size='medium'
            className=''
          >
            Login
          </Button>
        </Link>
      </div>
    </header>
  );
};
